import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 施設見学予約 API 操作クラス
 */
export default class InspectionReservations {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET InspectionReservation
   */
  public async get(userStore: LoginUser, inspectionReservationsSortKey: string): Promise<InspectionReservationsGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      inspectionReservationsSortKey: inspectionReservationsSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.InspectionReservation, formatParams);
    const response = await this.commonService.executeGet<InspectionReservationsGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET InspectionReservations
   */
  public async gets(userStore: LoginUser, queryParams?: { start?: string; end?: string }): Promise<Array<InspectionReservationsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.InspectionReservations, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<InspectionReservationsGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.IGNORE, 0);

    return response.data;
  }

  /**
   * ### POST InspectionReservation
   */
  public async post(userStore: LoginUser, requestBody: InspectionReservationsPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.InspectionReservations, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH InspectionReservation
   */
  public async patch(userStore: LoginUser, inspectionReservationsSortKey: string, requestBody: InspectionReservationsConsentedPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      inspectionReservationsSortKey: inspectionReservationsSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.InspectionReservation, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### DELETE InspectionReservation
   */
  public async delete(userStore: LoginUser, inspectionReservationsSortKey: string): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      inspectionReservationsSortKey: inspectionReservationsSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.InspectionReservation, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface InspectionReservationsGetModel {
  /** 施設を示す ID */
  facilityId: number;
  /** ソートキー */
  inspectionReservationsSortKey: string;

  /** 見学の申込日時 */
  applicationDatetime: number;
  /** 申込み種別 */
  applicationType: ApplicationType;
  /** 受付済みか否か */
  approved: boolean;
  /** 最後に受付済みにした日時 */
  latestApprovedDatetime: number | null;
  /** 最後に受付済みにしたユーザー ID */
  latestApprovedUserId: string | null;
  /** 最後に受付済みにしたユーザー名 */
  latestApprovedUserName: string | null;
  /** 見学日時 */
  datetime: number | null;
  /** 氏名 */
  name: string;
  /** 電話番号 */
  tel: string;
  /** 申込者からの連絡事項 */
  applicantMessage: string | null;
  /** 施設の見学予約に対する担当者 */
  customerName: string | null;
  /** 受付メモ */
  receptionMemo: string | null;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

export enum ApplicationType {
  /** 本人 */
  USER = "user",
  /** 施設ユーザー */
  FACILITY = "facility"
}

export interface InspectionReservationsPostModel {
  /** 見学の申込日時 */
  applicationDatetime: number;
  /** 申込み種別 */
  applicationType: ApplicationType;
  /** 受付済みか否か */
  approved: boolean;
  /** 見学日時 */
  datetime: number | null;
  /** 氏名 */
  name: string;
  /** 電話番号 */
  tel: string;
  /** 申込者からの連絡事項 */
  applicantMessage: string | null;
  /** 施設の見学予約に対する担当者 */
  customerName: string | null;
  /** 受付メモ */
  receptionMemo: string | null;
}

export interface InspectionReservationsConsentedPatchModel {
  /** 受付済みか否か */
  approved: boolean;
  /** 見学日時 */
  datetime: number | null;
  /** 受付メモ */
  receptionMemo: string | null;
  /** 施設の見学予約に対する担当者 */
  customerName: string | null;
  /** 最終更新日 */
  updated: number;
}
